import { NotifyHelper } from 'classes/helpers/notify.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { TeamSyncStatusExt } from 'lib_ts/interfaces/i-batrack';
import { isNil } from 'lodash';
import { AuthService } from 'services/auth.service';
import { create } from 'zustand';

type GSCtrlState = {
  enabled: boolean;
  override: Nullable<boolean>;
  statuses: TeamSyncStatusExt[];
  statusesUpdated: Date | undefined;
};

export type GSCtrlActions = {
  setEnabled: (v: boolean) => void;
  toggleEnabled: () => void;
  setOverride: (v: Nullable<boolean>, localOnly: boolean) => void;
  refreshStatuses: () => void;
};

type GSCtrlStore = GSCtrlState & GSCtrlActions;

export const useGSCtrlStore = create<GSCtrlStore>((set, get) => {
  const refreshStatuses = async () => {
    const statuses = await AuthService.getInstance().getGameStatuses();

    set({
      statuses: statuses.sort((a, b) => StringHelper.safeSort(a.team, b.team)),
      statusesUpdated: new Date(),
    });
  };

  return {
    enabled: false,
    override: null,
    statuses: [],
    statusesUpdated: undefined,
    setEnabled: (v) => set({ enabled: v }), // for first sync w/ server state
    toggleEnabled: async () => {
      const nextValue =
        await AuthService.getInstance().toggleGameStatusChecks();

      if (typeof nextValue !== 'boolean') {
        return;
      }

      NotifyHelper.success({
        message_md: `Check game status ${nextValue ? 'enabled' : 'disabled'}.`,
      });

      set({ enabled: nextValue });

      if (nextValue) {
        return;
      }

      const { override } = get();

      if (override === null) {
        // no override, nothing to reset
        return;
      }

      // when disabling checks, clear override value (if any)
      const resetOverrideSuccess =
        await AuthService.getInstance().setGameStatusOverride(null);

      if (!resetOverrideSuccess) {
        return;
      }

      // reset local override state to null
      set({ override: null });

      // auto-refresh after changing the override
      refreshStatuses();
    },
    setOverride: async (v, localOnly) => {
      if (localOnly) {
        // i.e. for first sync w/ server state
        set({ override: v });
        return;
      }

      const success = await AuthService.getInstance().setGameStatusOverride(
        v ?? null
      );

      if (success) {
        NotifyHelper.success({
          message_md: !isNil(v)
            ? `Game status override set to "${v ? 'In Progress' : 'No Game'}".`
            : 'Game status override cleared.',
        });
        set({ override: v });

        // auto-refresh after changing the override
        refreshStatuses();
      }
    },
    refreshStatuses: refreshStatuses,
  };
});
